import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Block, UnfinishedDecision } from '@/types';
import styles from '../ShutdownRitual.module.css';

interface UnfinishedStepProps {
  unfinishedBlocks: Block[];
  decisions: Record<string, UnfinishedDecision>;
  onDecision: (blockId: string, decision: UnfinishedDecision) => void;
}

function formatTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const date = new Date();
  date.setHours(hours, mins, 0, 0);
  return format(date, 'h:mm a');
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
};

export function UnfinishedStep({ unfinishedBlocks, decisions, onDecision }: UnfinishedStepProps) {
  const handledCount = Object.keys(decisions).length;
  const totalCount = unfinishedBlocks.length;

  return (
    <motion.div
      className={styles.stepContent}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h2 className={styles.stepTitle} variants={itemVariants}>
        Handle Unfinished Work
      </motion.h2>
      <motion.p className={styles.stepSubtitle} variants={itemVariants}>
        Decide what to do with each incomplete block. Carry them over, reschedule, or let them go.
      </motion.p>

      {unfinishedBlocks.length > 0 && (
        <motion.p
          variants={itemVariants}
          style={{ fontSize: '12px', color: '#888', marginBottom: '20px', textAlign: 'center' }}
        >
          {handledCount} of {totalCount} decisions made
        </motion.p>
      )}

      {unfinishedBlocks.length > 0 ? (
        <div className={styles.blockList}>
          {unfinishedBlocks.map((block, index) => {
            const currentDecision = decisions[block.id];
            return (
              <motion.div
                key={block.id}
                className={styles.blockItem}
                style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px' }}
                variants={itemVariants}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    className={styles.blockColor}
                    style={{ backgroundColor: block.color }}
                    aria-hidden="true"
                  />
                  <div className={styles.blockInfo}>
                    <p className={styles.blockTitle}>{block.title}</p>
                    <p className={styles.blockTime}>
                      {formatTime(block.startTime)} - {formatTime(block.startTime + block.duration)}
                    </p>
                  </div>
                </div>

                <div
                  className={styles.decisionButtons}
                  role="group"
                  aria-label={`Decision for ${block.title}`}
                >
                  <button
                    className={`${styles.decisionBtn} ${currentDecision === 'carry-over' ? styles.active : ''}`}
                    onClick={() => onDecision(block.id, 'carry-over')}
                    aria-pressed={currentDecision === 'carry-over'}
                  >
                    Carry Over
                  </button>
                  <button
                    className={`${styles.decisionBtn} ${currentDecision === 'reschedule' ? styles.activeReschedule : ''}`}
                    onClick={() => onDecision(block.id, 'reschedule')}
                    aria-pressed={currentDecision === 'reschedule'}
                  >
                    Reschedule
                  </button>
                  <button
                    className={`${styles.decisionBtn} ${currentDecision === 'delete' ? styles.activeDelete : ''}`}
                    onClick={() => onDecision(block.id, 'delete')}
                    aria-pressed={currentDecision === 'delete'}
                  >
                    Delete
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <motion.div className={styles.emptyState} variants={itemVariants}>
          <p>Great job! All your blocks for today are complete. Nothing to handle here.</p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default UnfinishedStep;
